/**
 * Who tells the loading screen what the boot is doing.
 *
 * Two rules live here, and they are here rather than inline in `scene-boot.ts`
 * because each is a claim about the boot's own record that a test should be able
 * to hold to on a machine with no GPU:
 *
 * 1. **One `performance.mark` per phase, at the phase's start.** The whole of
 *    `docs/engineering/fly-boot-timing.md` is read off those marks, and a
 *    measurement report is not a phase starting — it is the phase already
 *    running saying more about itself. A second mark for the same name would put
 *    a duration of zero in that table.
 * 2. **A measurement reports under its own phase's label or not at all.** The
 *    boot's three fetches are started together and awaited in order
 *    (`scene-boot.ts`), so the airframe's bytes begin arriving while the label
 *    still says `LOADING THE GUNS`. Forwarding them then would jump the bar into
 *    the airframe's segment under the wrong caption. They are held instead, and
 *    the phase announcement carries whatever has arrived by the time it happens.
 *    `precompile` cannot be early in this way — its work starts when its phase
 *    does — but it goes through the same gate, because the rule is about which
 *    caption a number is allowed to move the bar under, not about who reports.
 *
 * Nothing here imports three.js, for the same reason `boot-progress.ts` does
 * not: the boot's *bookkeeping* should be testable on a machine with no adapter.
 */

import { bootProgressFor, type BootMeasure, type BootPhase, type BootProgress } from "./boot-progress.ts";

/** The one callback this needs from `FlightSceneCallbacks`, named structurally so no scene type is imported. */
export interface BootProgressSink {
  readonly onBootProgress?: (progress: BootProgress) => void;
}

export interface BootReporter {
  /** The boot has moved on to `phase`: mark it once, and report it with whatever it has already measured about itself. */
  phase(phase: BootPhase): void;
  /** A fresh measurement of `phase`'s own work. Silent until `phase` has been announced. */
  measure(phase: BootPhase, done: number, total: number): void;
  /**
   * A standing fact to print under the bar for the rest of the boot — see
   * `BootProgress.note`. Sticky rather than a one-shot report, because the
   * thing it says (`RENDERER: WEBGL`) is true of every phase after it, and a
   * caption that vanished on the next phase would be a flicker nobody could
   * read. Re-reports the phase already running so the line appears at once.
   */
  note(text: string): void;
}

export function createBootReporter(callbacks: BootProgressSink): BootReporter {
  const marked = new Set<BootPhase>();
  const counts = new Map<BootPhase, BootMeasure>();
  let standing: string | undefined;
  let current: BootPhase | undefined;
  const report = (phase: BootPhase): void => {
    current = phase;
    callbacks.onBootProgress?.(bootProgressFor(phase, counts.get(phase), standing));
  };
  return {
    phase: (phase) => {
      if (!marked.has(phase)) {
        marked.add(phase);
        performance.mark(`fly-boot:${phase}`);
      }
      report(phase);
    },
    measure: (phase, done, total) => {
      counts.set(phase, { done, total });
      if (marked.has(phase)) report(phase);
    },
    note: (text) => {
      standing = text;
      // Silent before the first phase — there is no report to attach a note to
      // yet, and the phase announcement that follows will carry it.
      if (current !== undefined) report(current);
    },
  };
}
